import React, { useState, useEffect } from "react";
import {
  Card,
  Typography,
  Steps,
  Button,
  Result,
  message,
  Spin,
  Space,
  Alert,
} from "antd";
import {
  ScanOutlined,
  CheckCircleOutlined,
  UserOutlined,
  AimOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import QrScanner from "../../components/qrscanner/QrScanner";
import { joinWaitingRoom } from "../../api/waitingroom";
import { WaitingRoom } from "../../api/waitingRooms.api";
import {
  getWaitingRoomCache,
  isInWaitingRoom,
} from "../../utils/waitingRoomCache";

const { Title, Paragraph, Text } = Typography;
const { Step } = Steps;

interface CachedWaitingRoomStatus {
  code: string;
  isPriority?: boolean;
  timestamp?: number;
}

const JoinWaitingRoomPage: React.FC = () => {
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [joinResult, setJoinResult] = useState<WaitingRoom | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [cachedStatus, setCachedStatus] =
    useState<CachedWaitingRoomStatus | null>(null);
  const navigate = useNavigate();

  // Check cache on component mount
  useEffect(() => {
    const cached = getWaitingRoomCache();
    if (cached) {
      setCachedStatus(cached);
    }
  }, []);

  const handleScanSuccess = async (_qrData: string) => {
    void _qrData;

    // Check cache before attempting to join
    if (isInWaitingRoom()) {
      const cached = getWaitingRoomCache();
      setError(
        `Ya estás en la sala de espera con el código ${cached?.code}. Puedes unirte nuevamente en 2 horas.`
      );
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await joinWaitingRoom();
      message.success("Te has unido a la sala de espera correctamente");
      setJoinResult(result);
      setCurrentStep(2);
      // Update cached status
      setCachedStatus(getWaitingRoomCache());
    } catch (err) {
      const msg =
        err instanceof Error
          ? err.message
          : "Error al unirse a la sala de espera";
      setError(msg);
      message.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleScanError = (errorMessage: string) => {
    console.warn("Scanner error reported to page:", errorMessage);

    // Show more user-friendly messages based on error content
    if (errorMessage.includes("permiso") || errorMessage.includes("denied")) {
      setError(
        "Necesitamos acceso a tu cámara para escanear el código QR. Por favor, acepta el permiso en tu navegador y reintenta."
      );
    } else if (
      errorMessage.includes("no camera") ||
      errorMessage.includes("encontró")
    ) {
      setError(
        "No se detectó una cámara en tu dispositivo. Por favor, verifica que tu cámara esté funcionando correctamente."
      );
    } else {
      setError(errorMessage);
    }

    // Don't automatically move away from the scanner screen
    // Let the user decide if they want to go back or retry
  };

  // Add a method for manual fallback
  const handleManualJoin = async () => {
    // Check cache before attempting to join
    if (isInWaitingRoom()) {
      const cached = getWaitingRoomCache();
      setError(
        `Ya estás en la sala de espera con el código ${cached?.code}. Puedes unirte nuevamente en 2 horas.`
      );
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await joinWaitingRoom();
      message.success("Te has unido a la sala de espera correctamente");
      setJoinResult(result);
      setCurrentStep(2);
      // Update cached status
      setCachedStatus(getWaitingRoomCache());
    } catch (err) {
      const msg =
        err instanceof Error
          ? err.message
          : "Error al unirse a la sala de espera";
      setError(msg);
      message.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const startScanning = () => {
    // Check cache before starting scan
    if (isInWaitingRoom()) {
      const cached = getWaitingRoomCache();
      setError(
        `Ya estás en la sala de espera con el código ${cached?.code}. Puedes unirte nuevamente en 2 horas.`
      );
      return;
    }

    setCurrentStep(1);
    setError(null);
  };

  const returnToHome = () => {
    navigate("/perfil");
  };

  // Show cached status if user is already in waiting room
  if (cachedStatus && !joinResult) {
    return (
      <Card
        style={{
          maxWidth: "100%",
          width: "100%",
          margin: "0.5rem",
          padding: "0.5rem",
        }}
      >
        <Result
          icon={<ClockCircleOutlined style={{ color: "#52c41a" }} />}
          title="Ya estás en la sala de espera"
          subTitle={
            <Space direction="vertical">
              <Text strong>
                Tu código de atención: <Text mark>{cachedStatus.code}</Text>
              </Text>
              {cachedStatus.isPriority && (
                <Text type="success">Tienes prioridad por cita programada</Text>
              )}
              <Text type="secondary">
                Podrás unirte nuevamente en 2 horas si es necesario.
              </Text>
            </Space>
          }
          extra={[
            <Button type="primary" key="home" onClick={returnToHome}>
              Ir al Perfil
            </Button>,
          ]}
        />
      </Card>
    );
  }

  // Renderizado condicional basado en el paso actual
  const renderContent = () => {
    switch (currentStep) {
      case 0: // Paso inicial de instrucciones
        return (
          <Space direction="vertical" style={{ width: "100%" }} size="large">
            <Title level={3}>Unirse a Sala de Espera</Title>
            <Paragraph>
              Para registrarte en la sala de espera, escanea el código QR
              ubicado en la recepción de la clínica.
            </Paragraph>
            {isInWaitingRoom() && (
              <Alert
                message="Ya estás en la sala de espera"
                description={`Tienes el código ${
                  getWaitingRoomCache()?.code
                }. No puedes unirte nuevamente por 2 horas.`}
                type="warning"
                showIcon
              />
            )}
            <Button
              type="primary"
              icon={<ScanOutlined />}
              onClick={startScanning}
              size="large"
              block
              disabled={isInWaitingRoom()}
            >
              Comenzar Escaneo
            </Button>
          </Space>
        );

      case 1: // Paso de escaneo QR
        return (
          <Space direction="vertical" style={{ width: "100%" }} size="large">
            <QrScanner
              onScanSuccess={handleScanSuccess}
              onScanError={handleScanError}
              onCancel={() => setCurrentStep(0)}
            />
            {loading && (
              <div style={{ textAlign: "center", padding: "20px 0" }}>
                <Spin size="large" />
                <Paragraph style={{ marginTop: 16 }}>
                  Procesando tu solicitud...
                </Paragraph>
              </div>
            )}

            {/* Add fallback button for when camera access fails */}
            {!loading && (
              <Button onClick={handleManualJoin} type="default" block>
                Problemas con la cámara? Unirse directamente
              </Button>
            )}

            <Button onClick={() => setCurrentStep(0)}>
              Volver a Instrucciones
            </Button>
          </Space>
        );

      case 2: // Paso de confirmación
        return (
          <Result
            status="success"
            title="¡Te has unido a la sala de espera!"
            subTitle={
              <>
                {joinResult?.code && (
                  <Text strong>
                    Tu código de atención: <Text mark>{joinResult.code}</Text>
                  </Text>
                )}
                <br />
                {joinResult?.isPriority ? (
                  <Text type="success">
                    Se ha detectado una cita programada. Tienes prioridad.
                  </Text>
                ) : (
                  <Text>No se detectó una cita programada para hoy.</Text>
                )}
              </>
            }
            extra={[
              <Button type="primary" key="home" onClick={returnToHome}>
                Ir al Perfil
              </Button>,
            ]}
          />
        );

      default:
        return <div>Paso no reconocido</div>;
    }
  };

  return (
    <Card
      style={{
        maxWidth: "100%",
        width: "100%",
        margin: "0.5rem",
        padding: "0.5rem",
      }}
    >
      <Steps current={currentStep} style={{ marginBottom: 24 }}>
        <Step title="Instrucciones" icon={<UserOutlined />} />
        <Step title="Escanear QR" icon={<AimOutlined />} />
        <Step title="Confirmación" icon={<CheckCircleOutlined />} />
      </Steps>

      {error && (
        <div style={{ marginBottom: 16 }}>
          <Result
            status="error"
            title="Ha ocurrido un error"
            subTitle={error}
            extra={[
              <Button
                type="primary"
                key="retry"
                onClick={startScanning}
                disabled={isInWaitingRoom()}
              >
                Intentar nuevamente
              </Button>,
            ]}
          />
        </div>
      )}

      {!error && renderContent()}
    </Card>
  );
};

export default JoinWaitingRoomPage;
